import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { listAudit } from '@/features/admin/admin-api';
import { useFormatters } from '@/i18n/format';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Activity } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/admin/audit')({
  component: AdminAuditPage,
});

function AdminAuditPage() {
  const { t } = useTranslation();
  const { dateTime } = useFormatters();
  const [orgId, setOrgId] = useState('');
  const [userId, setUserId] = useState('');
  const [action, setAction] = useState('');

  const audit = useQuery({
    queryKey: ['admin', 'audit', orgId, userId, action],
    queryFn: () =>
      listAudit({
        orgId: orgId || undefined,
        userId: userId || undefined,
        action: action || undefined,
        limit: 200,
      }),
  });
  const entries = audit.data?.entries ?? [];

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('admin.nav.adminPanel', { defaultValue: 'Admin' })}
        title={t('admin.audit.title')}
        description={t('admin.audit.description')}
      />

      <Card className="border-border/70 p-4 shadow-soft">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="f-org">{t('admin.audit.orgIdLabel')}</Label>
            <Input id="f-org" value={orgId} onChange={(e) => setOrgId(e.target.value.trim())} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="f-user">{t('admin.audit.userIdLabel')}</Label>
            <Input id="f-user" value={userId} onChange={(e) => setUserId(e.target.value.trim())} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="f-action">{t('admin.audit.actionLabel')}</Label>
            <Input
              id="f-action"
              value={action}
              onChange={(e) => setAction(e.target.value.trim())}
              placeholder={t('admin.audit.actionPlaceholder')}
            />
          </div>
        </div>
      </Card>

      {audit.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 rounded-lg" />
          <Skeleton className="h-12 rounded-lg" />
          <Skeleton className="h-12 rounded-lg" />
        </div>
      ) : entries.length === 0 ? (
        <EmptyState icon={Activity} title={t('admin.audit.empty')} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border/70 shadow-soft">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5 text-left">{t('admin.audit.headers.time')}</th>
                <th className="px-4 py-2.5 text-left">{t('admin.audit.headers.action')}</th>
                <th className="px-4 py-2.5 text-left">{t('admin.audit.headers.actor')}</th>
                <th className="px-4 py-2.5 text-left">{t('admin.audit.headers.org')}</th>
                <th className="px-4 py-2.5 text-left">{t('admin.audit.headers.resource')}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr
                  key={e.id}
                  className="border-t border-border/60 transition-colors hover:bg-muted/30"
                >
                  <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                    {dateTime(e.createdAt)}
                  </td>
                  <td className="px-4 py-2">
                    <Badge variant="outline" className="font-mono text-[10px]">
                      {e.action}
                    </Badge>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">
                    <span className="text-muted-foreground">{e.actorKind}:</span> {e.actorId ?? '—'}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{e.orgId ?? '—'}</td>
                  <td className="px-4 py-2 font-mono text-xs">
                    <span className="text-muted-foreground">{e.resourceKind ?? '—'}:</span>{' '}
                    {e.resourceId ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
